import type { IsoWeekday, OperatingSchedule, OperatingStatus } from './types.js';
import { OPERATING_SCHEDULE_SCHEMA_VERSION } from './types.js';

const TIME_RE = /^([01]\d|2[0-3]):([0-5]\d)$/;
const CLOSED_BEHAVIORS = ['leave', 'sleep', 'observable'] as const;
const DEVICE_ELIGIBILITY_EDGE_MINUTES = 10;

export interface ScheduleValidationResult {
  valid: boolean;
  errors: string[];
}

export interface LocalDateTimeParts {
  /** ISO weekday 1=Mon … 7=Sun. */
  weekday: IsoWeekday;
  /** Minutes since midnight 0–1439. */
  minutesSinceMidnight: number;
}

export interface OperatingStatusResult {
  status: OperatingStatus;
  reason:
    | 'no_hours'
    | 'closed_day'
    | 'before_open'
    | 'after_close'
    | 'open'
    | 'overnight_carryover';
}

export function parseTimeToMinutes(time: string): number | null {
  const m = TIME_RE.exec(time);
  if (!m) return null;
  return Number(m[1]) * 60 + Number(m[2]);
}

export function isValidTimeString(time: string): boolean {
  return parseTimeToMinutes(time) !== null;
}

export function detectOvernight(openTime: string, closeTime: string): boolean {
  const open = parseTimeToMinutes(openTime);
  const close = parseTimeToMinutes(closeTime);
  if (open === null || close === null) return false;
  return close <= open;
}

export function previousIsoWeekday(day: IsoWeekday): IsoWeekday {
  return (day === 1 ? 7 : (day - 1)) as IsoWeekday;
}

export function validateOperatingSchedule(
  schedule: OperatingSchedule,
): ScheduleValidationResult {
  const errors: string[] = [];

  if (schedule.schemaVersion !== OPERATING_SCHEDULE_SCHEMA_VERSION) {
    errors.push(`Unsupported schedule schema: ${schedule.schemaVersion}`);
  }

  if (schedule.timezoneStrategy === 'explicit' && !schedule.timezone?.trim()) {
    errors.push('Explicit timezone strategy requires timezone');
  }

  if (!schedule.hasOperatingHours) {
    return { valid: errors.length === 0, errors };
  }

  if (!CLOSED_BEHAVIORS.includes(schedule.closedBehavior)) {
    errors.push(`Invalid closedBehavior: ${schedule.closedBehavior}`);
  }

  if (schedule.daysOpen.length === 0) {
    errors.push('At least one open day is required when operating hours are enabled');
  }

  for (const day of schedule.daysOpen) {
    if (day < 1 || day > 7) errors.push(`Invalid weekday: ${day}`);
  }

  if (!isValidTimeString(schedule.openTime)) {
    errors.push(`Invalid openTime: ${schedule.openTime}`);
  }
  if (!isValidTimeString(schedule.closeTime)) {
    errors.push(`Invalid closeTime: ${schedule.closeTime}`);
  }

  const overnight = detectOvernight(schedule.openTime, schedule.closeTime);
  if (schedule.overnight !== overnight) {
    errors.push('overnight flag does not match open/close times');
  }

  return { valid: errors.length === 0, errors };
}

/** Whether `minutes` falls inside the daily open window (handles overnight). */
export function isMinuteWithinOpenWindow(
  minutes: number,
  openMinutes: number,
  closeMinutes: number,
  overnight: boolean,
): boolean {
  if (!overnight) {
    if (openMinutes === closeMinutes) return false;
    return minutes >= openMinutes && minutes < closeMinutes;
  }
  return minutes >= openMinutes || minutes < closeMinutes;
}

export function resolveOperatingStatus(
  schedule: OperatingSchedule,
  local: LocalDateTimeParts,
): OperatingStatusResult {
  if (!schedule.hasOperatingHours) {
    return { status: 'open', reason: 'no_hours' };
  }

  const openMin = parseTimeToMinutes(schedule.openTime);
  const closeMin = parseTimeToMinutes(schedule.closeTime);
  if (openMin === null || closeMin === null) {
    return { status: 'closed', reason: 'after_close' };
  }

  const { weekday, minutesSinceMidnight: minutes } = local;

  if (!schedule.overnight) {
    if (!schedule.daysOpen.includes(weekday)) {
      return { status: 'closed', reason: 'closed_day' };
    }
    const open = isMinuteWithinOpenWindow(minutes, openMin, closeMin, false);
    if (open) return { status: 'open', reason: 'open' };
    return minutes < openMin
      ? { status: 'closed', reason: 'before_open' }
      : { status: 'closed', reason: 'after_close' };
  }

  // Overnight: early-morning hours belong to the previous calendar day's session.
  if (minutes < closeMin) {
    const prevDay = previousIsoWeekday(weekday);
    if (schedule.daysOpen.includes(prevDay)) {
      return { status: 'open', reason: 'overnight_carryover' };
    }
    return { status: 'closed', reason: 'closed_day' };
  }

  if (minutes >= openMin) {
    if (schedule.daysOpen.includes(weekday)) {
      return { status: 'open', reason: 'open' };
    }
    return { status: 'closed', reason: 'closed_day' };
  }

  return { status: 'closed', reason: 'after_close' };
}

/** Transition helper: returns new status when clock advances (pure). */
export function transitionOperatingStatus(
  schedule: OperatingSchedule,
  from: LocalDateTimeParts,
  to: LocalDateTimeParts,
): { from: OperatingStatus; to: OperatingStatus; changed: boolean } {
  const fromStatus = resolveOperatingStatus(schedule, from).status;
  const toStatus = resolveOperatingStatus(schedule, to).status;
  return { from: fromStatus, to: toStatus, changed: fromStatus !== toStatus };
}

/** ISO weekday from JS Date (local interpretation left to caller). */
export function isoWeekdayFromDate(date: Date): IsoWeekday {
  const js = date.getDay();
  return (js === 0 ? 7 : js) as IsoWeekday;
}

export function localPartsFromDate(date: Date): LocalDateTimeParts {
  return {
    weekday: isoWeekdayFromDate(date),
    minutesSinceMidnight: date.getHours() * 60 + date.getMinutes(),
  };
}

/** Live-session length in minutes (24/7 → full calendar day). */
export function liveSessionDurationMinutes(schedule: OperatingSchedule): number {
  if (!schedule.hasOperatingHours) return 24 * 60;
  const openMin = parseTimeToMinutes(schedule.openTime);
  const closeMin = parseTimeToMinutes(schedule.closeTime);
  if (openMin === null || closeMin === null) return 0;
  if (!schedule.overnight) return closeMin - openMin;
  return 24 * 60 - openMin + closeMin;
}

/** Minutes elapsed since today's open within the current live session, or null when closed. */
export function minutesIntoLiveSession(
  schedule: OperatingSchedule,
  local: LocalDateTimeParts,
): number | null {
  const status = resolveOperatingStatus(schedule, local);
  if (status.status !== 'open') return null;

  if (!schedule.hasOperatingHours) {
    return local.minutesSinceMidnight;
  }

  const openMin = parseTimeToMinutes(schedule.openTime);
  const closeMin = parseTimeToMinutes(schedule.closeTime);
  if (openMin === null || closeMin === null) return null;

  const { minutesSinceMidnight: minutes } = local;
  if (!schedule.overnight) return minutes - openMin;

  if (status.reason === 'overnight_carryover') {
    return 24 * 60 - openMin + minutes;
  }
  return minutes - openMin;
}

/** Notes §12 — devices eligible in first/last 10 live minutes (none while closed). */
export function isWithinDeviceEligibilityWindow(
  schedule: OperatingSchedule,
  local: LocalDateTimeParts,
): boolean {
  const minutesIn = minutesIntoLiveSession(schedule, local);
  if (minutesIn === null) return false;
  const duration = liveSessionDurationMinutes(schedule);
  const edge = DEVICE_ELIGIBILITY_EDGE_MINUTES;
  if (duration <= edge * 2) return true;
  return minutesIn < edge || minutesIn >= duration - edge;
}
